// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Tag } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Coupon {
  id: string;
  code: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  is_active: boolean;
}

export const EmailTestPanel: React.FC = () => {
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [selectedCoupon, setSelectedCoupon] = useState<string>('');
  const [coupons, setCoupons] = useState<Coupon[]>([]);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const { data, error } = await supabase
        .from('coupons')
        .select('id, code, description, discount_type, discount_value, is_active')
        .eq('is_active', true)
        .order('code');

      if (error) throw error;
      setCoupons((data as any) || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const testEmailTrigger = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address first');
      return;
    }
    try {
      // Use existing campaign and template IDs
      const campaignId = '1ac1550c-6001-4302-84ff-d69b933d855d'; // Athlete Profile Creation Notifications
      const templateId = 'aba9264c-f364-4c85-a9c1-0a0af4458e31';  // From our working email
      
      // Get selected coupon details
      let couponData = null;
      if (selectedCoupon) {
        const coupon = coupons.find(c => c.id === selectedCoupon);
        if (coupon) {
          couponData = {
            coupon_code: coupon.code,
            coupon_name: coupon.description || coupon.code,
            discount_type: coupon.discount_type,
            discount_value: coupon.discount_value,
            coupon_link: `https://recruitlook2.com/signup?coupon=${coupon.code}`
          };
        }
      }

      // Create test email with correct table structure
      const testEmailData = {
        campaign_id: campaignId,
        template_id: templateId,
        recipient_email: testEmail,
        type: 'email',
        subject: 'Test - Complete Your Athlete Profile',
        content: `Hi there, this is a test of the athlete profile creation notification system sent to ${testEmail}.${couponData ? `\n\nUse coupon code ${couponData.coupon_code} for a ${couponData.discount_type === 'percentage' ? couponData.discount_value + '%' : '$' + couponData.discount_value} discount!\nSign up here: ${couponData.coupon_link}` : ''}`,
        status: 'pending',
        recipient_id: null,
        recipient_phone: testPhone || null,
        external_id: null,
        metadata: couponData || {}
      };

      const { error } = await supabase
        .from('communication_logs')
        .insert(testEmailData);

      if (error) {
        console.error('Error inserting test email:', error);
        toast.error(`Failed to queue test email: ${error.message}`);
        return;
      }

      toast.success('Test email queued successfully!');
    } catch (error) {
      console.error('Error in test email trigger:', error);
      toast.error('Failed to trigger test email');
    }
  };

  const sendPendingEmails = async () => {
    try {
      const { data, error } = await invokeEdgeFunction('send-communication-email');

      if (error) {
        console.error('Error calling edge function:', error);
        toast.error(`Failed to send emails: ${error.message}`);
        return;
      }

      console.log('Edge function response:', data);
      toast.success(`Email sending completed: ${data.message}`);
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error('Failed to send emails');
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
      <h3 className="font-semibold">Email Testing Panel</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label htmlFor="testEmailPanel">Test Email Address</Label>
          <Input
            id="testEmailPanel"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="your@email.com"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="testPhonePanel">Test Phone Number (Optional)</Label>
          <Input
            id="testPhonePanel"
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="(555) 123-4567"
            formatPhone
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="couponSelect" className="flex items-center gap-2">
          <Tag className="h-4 w-4" />
          Include Coupon (Optional)
        </Label>
        <Select value={selectedCoupon} onValueChange={setSelectedCoupon}>
          <SelectTrigger id="couponSelect" className="bg-background">
            <SelectValue placeholder="Select a coupon to include" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="none">No Coupon</SelectItem>
            {coupons.map((coupon) => (
              <SelectItem key={coupon.id} value={coupon.id}>
                {coupon.code} - {coupon.description || coupon.code} ({coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`} off)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedCoupon && selectedCoupon !== 'none' && (
          <p className="text-xs text-muted-foreground">
            Coupon link will be included in email metadata and content
          </p>
        )}
      </div>
      
      <div className="flex gap-2">
        <Button onClick={testEmailTrigger} variant="outline" disabled={!testEmail}>
          Queue Test Email
        </Button>
        <Button onClick={sendPendingEmails}>
          Send Pending Emails
        </Button>
      </div>
    </div>
  );
};